import { useApi, useMount, useModal, useRouter } from "hooks";
import React, { useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import lang from "translations";
import ResourceTypeForm from "../resource-type-form/resource-type-form";
import { getResourceType, editResourceType, deleteResourceType } from "apis";
import { resourceTypeDetailResponse, resourceTypeRequest } from "mappers";
import { mixpanel, TrackEvent } from "mixpanel";
import { VenueContext } from "contexts";
import { Icon, Text, Title, Toast } from "components/commons";
import { Path } from "paths";
import { DeleteModal } from "components/modals";

const EditResourceType = () => {
  const { id } = useParams();
  const { venue } = useContext(VenueContext);
  const { venueId } = venue;
  const deleteModal = useModal();
  const { history } = useRouter();

  const { request: getRequest, mappedData: initialData } = useApi({
    api: getResourceType,
    mapper: resourceTypeDetailResponse,
    params: { venueId },
    handleOwnError: true,
  });

  const { request: editRequest, loading: submitting } = useApi({
    api: editResourceType,
    handleOwnError: {
      badrequest: true,
    },
    paramsMapper: resourceTypeRequest,
  });

  const { request: requestDeleteResourceType } = useApi({
    api: () => deleteResourceType({ resourceTypeId: id }),
  });

  useMount(() => {
    getRequest({ resourceTypeId: id });
    mixpanel.track(TrackEvent.VisitedPage, {
      Page: lang.editResourceType,
    });
  });

  const submitForm = useCallback(
    async (params) => {
      try {
        const res = await editRequest({ ...params, id });
        return {
          response: res,
          message: lang.populate(lang.resourceUpdated, [params.name]),
        };
      } catch (e) {
        throw e;
      }
    },
    [editRequest, id]
  );

  return (
    <>
      <ResourceTypeForm
        submit={submitForm}
        title={lang.editResourceType}
        submitting={submitting}
        initialState={initialData}
        deleteResourceTypeButton={{
          text: lang.deleteResourceType,
          onClick: () => {
            deleteModal.show({
              ok: async () => {
                mixpanel.track(TrackEvent.ClickedButton, {
                  Button: lang.deleteResourceType,
                  Page: lang.editResourceType,
                });

                await requestDeleteResourceType();
                Toast({
                  content: lang.populate(lang.resourceTypeDeleted, [initialData.name]),
                  success: true,
                  icon: "check",
                }).open();
                history.push(Path.RESOURCE_TYPE);
              },
            });
          },
        }}
      />
      <DeleteModal
        primaryText={lang.deleteResourceType}
        customHeader={
          <div className="flex items-center justify-between">
            <Title lg className="mb-sm pt-md">
              {lang.deleteResourceType + "?"}
            </Title>
            <Icon
              className="outline-none text-xl text-gray hover:text-gray cursor-pointer"
              name="remove"
              onClick={() => {
                deleteModal.close();
              }}
            />
          </div>
        }
        customContent={
          <>
            <Text>
              {lang.populate(lang.areYouSureYouWantToDeleteResourceType, [
                initialData.name,
              ])}
            </Text>
            <br></br>
            <Text>{lang.deletingThisResourceType}</Text>
          </>
        }
        {...deleteModal}
      />
    </>
  );
};

export default EditResourceType;
